import request from "../../utils/request";
import { Result } from "../../models/general";
import { ScheduledShiftView } from "../../types/scheduling";
import {
  ShiftSwapRequestInput,
  ShiftSwapDecisionInput,
  ShiftSwapRequestView,
  ShiftSwapHistoryQuery,
  PagedShiftSwaps,
} from "../../models/shiftSwap";

// Create a new shift-swap request.
export async function requestShiftSwap(input: ShiftSwapRequestInput) {
  const response = await request.post<Result<ShiftSwapRequestView>>("Shift/Swap/Request", input);
  return response.data;
}

// Swaps I'm involved in (as requester or counterparty).
export async function getMyShiftSwaps() {
  const response = await request.get<Result<ShiftSwapRequestView[]>>("Shift/Swap/My");
  return response.data;
}

// Swaps awaiting my managerial approval.
export async function getPendingShiftSwaps() {
  const response = await request.get<Result<ShiftSwapRequestView[]>>("Shift/Swap/Pending");
  return response.data;
}

// Read-only, server-paged team history for managers/HR (terminal + in-flight swaps).
export async function getShiftSwapHistory(query: ShiftSwapHistoryQuery = {}) {
  const response = await request.get<Result<PagedShiftSwaps>>("Shift/Swap/History", { params: query });
  return response.data;
}

// Counterparty accepts a swap request directed at them.
export async function acceptShiftSwap(id: number) {
  const response = await request.post<Result<ShiftSwapRequestView>>(`Shift/Swap/${id}/Accept`);
  return response.data;
}

// Counterparty declines a swap request directed at them.
export async function declineShiftSwap(id: number) {
  const response = await request.post<Result<ShiftSwapRequestView>>(`Shift/Swap/${id}/Decline`);
  return response.data;
}

// Manager approves a peer-accepted swap.
export async function approveShiftSwap(id: number, input?: ShiftSwapDecisionInput) {
  const response = await request.post<Result<ShiftSwapRequestView>>(`Shift/Swap/${id}/Approve`, input ?? {});
  return response.data;
}

// Manager rejects a swap.
export async function rejectShiftSwap(id: number, input?: ShiftSwapDecisionInput) {
  const response = await request.post<Result<ShiftSwapRequestView>>(`Shift/Swap/${id}/Reject`, input ?? {});
  return response.data;
}

// Requester cancels their own pending/peer-accepted swap.
export async function cancelShiftSwap(id: number) {
  const response = await request.post<Result<boolean>>(`Shift/Swap/${id}/Cancel`);
  return response.data;
}

// Future, not-yet-worked shifts for a colleague OR self (same company).
// `from`/`to` are ISO-8601 datetime strings.
export async function getSwapEligibleShifts(userId: string, from: string, to: string) {
  const response = await request.get<Result<ScheduledShiftView[]>>("Shift/Swap/Shifts", {
    params: { userId, from, to },
  });
  return response.data;
}

export interface SwapColleague {
  id: string;
  fullName: string;
  imageUrl?: string | null;
}

// Colleagues the current user may request a swap with (swap-enabled, same company,
// excludes self). Works for Clocking employees — unlike Account/Users, which returns
// nothing for a role that supervises no one.
export async function getSwappableColleagues() {
  const response = await request.get<Result<SwapColleague[]>>("Shift/Swap/Colleagues");
  return response.data;
}
